import { qualifier } from "$root/utils";
import { ClickableDiv, Column, Icon, type IconName, Row, Text } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { ForEach } from "@mdotm/utils/react-extra";
import { Checkbox, IconButton } from "@mdotm/shadcn/mdotm/components";

type CheckboxToggleProps =
	| { checked: boolean; onToggle(checked: boolean): void; disableToggle?: boolean }
	| { checked?: undefined; onToggle?: undefined };

export function SidePanelListItem(
	props: {
		title: string;
		count?: number;
		description?: string;
		overlayActions?: Array<{
			icon: IconName;
			onClick(): void;
		}>;
		selected?: boolean;
		dotColor?: string;
		size?: "small" | "large";
	} & CheckboxToggleProps &
		(
			| {
					onClick(): void;
					/** Identical behavior to onClick, but shows as ">" at the right of the item content */
					onDrillDown?: undefined;
			  }
			| {
					onClick?: undefined;
					/** Identical behavior to onClick, but shows as ">" at the right of the item content */
					onDrillDown(): void;
			  }
			| {
					onClick?: undefined;
					/** Identical behavior to onClick, but shows as ">" at the right of the item content */
					onDrillDown?: undefined;
			  }
		),
) {
	const isInteractive =
		props.onClick != null || props.onDrillDown != null || (props.onToggle != null && !props.disableToggle);
	const handleClick =
		props.onClick ??
		props.onDrillDown ??
		(props.onToggle != null && !props.disableToggle ? () => props.onToggle(!props.checked) : undefined);
	return (
		<ClickableDiv
			onClick={handleClick}
			className={{ "group flex-1 overflow-hidden": true, "cursor-pointer": isInteractive }}
			data-qualifier={qualifier.component.sidePanelClickableDiv("SidePanelListItem")}
		>
			<Row
				minSize="0"
				alignItems="center"
				gap={8}
				data-selected={props.selected}
				className={{
					"relative overflow-hidden py-2 px-2 rounded-sm": true,
					"min-h-12": props.size !== "small",
					"min-h-6": props.size === "small",
					"group-focus-visible:bg-(--palette-N50) hover:bg-(--palette-N50)": isInteractive,
					"data-[selected=true]:bg-(--palette-N100)": true,
				}}
			>
				{props.onToggle != null && (
					<Checkbox
						className={{
							"-my-2 -mx-2 [&>label]:px-2": true,
							"h-10": props.size !== "small",
							"h-6": props.size === "small",
						}}
						onClick={(e) => e.stopPropagation()}
						checked={props.checked}
						disabled={props.disableToggle}
						onChange={props.onToggle}
					/>
				)}
				<Column minSize="0" justifyContent="start" className="flex-1">
					<Row minSize="0" flexGrow={0} alignItems="center">
						<Text
							as="div"
							type={props.size === "small" ? "Body/M/Bold" : "Body/L/Bold"}
							title={props.title}
							className="truncate"
						>
							{props.title}
						</Text>
						{props.dotColor && <Icon className="mx-2" icon="driver-arrow-center" color={props.dotColor} />}
						<Text
							as="div"
							type={props.size === "small" ? "Body/M/Book" : "Body/L/Book"}
							title={props.count?.toString()}
							className={{
								"ml-auto": true,
								"group-hover:opacity-0 transition-opacity": Boolean(props.overlayActions?.length),
							}}
						>
							{props.count}
						</Text>
					</Row>
					<Text type={props.size === "small" ? "Body/S/Book" : "Body/M/Book"} title={props.description}>
						{props.description}
					</Text>
				</Column>
				{props.overlayActions && props.overlayActions.length > 0 && (
					<Row
						gap={8}
						className={{
							"absolute transition-opacity rounded-sm opacity-0 group-hover:opacity-100 shrink-0": true,
							"right-2": !props.onDrillDown,
							"right-6": !!props.onDrillDown && props.size === "small",
							"right-8": !!props.onDrillDown && props.size !== "small",
							"bg-(--palette-N100)": props.selected,
							"group-hover:bg-(--palette-N50) bg-(--palette-N0)": !props.selected,
						}}
					>
						<ForEach collection={props.overlayActions}>
							{(action) => (
								<IconButton
									className="p-0!"
									icon={action.icon}
									size={16}
									color={themeCSSVars.palette_N500}
									background={props.selected ? themeCSSVars.palette_N100 : themeCSSVars.palette_N50}
									hoverBackground={props.selected ? themeCSSVars.palette_N200 : themeCSSVars.palette_N100}
									onClick={(e) => {
										e.stopPropagation();
										action.onClick();
									}}
								/>
							)}
						</ForEach>
					</Row>
				)}
				{props.onDrillDown && <Icon icon="Right" size={16} color={themeCSSVars.palette_N500} className="shrink-0" />}
			</Row>
		</ClickableDiv>
	);
}
